// @ts-strict-ignore
import * as React from 'react';

import { trackEvent } from '@/analytics/trackEvent';
import { useRefetchReadDeeplinks } from '@/features/CustomFeedV2/hooks/useFetchReadDeeplinks';
import { useDevices } from '@/hooks/useDevices';
import { usePopupBlockedMessaging } from '@/hooks/usePopupBlockedMessaging';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { FeedItemType } from '@/types';
import { downloadDocument } from '@/utils/downloadDocument';
import { downloadUtil } from '@/utils/downloadUtil';
import { fetchAndFormatDocument } from '@/utils/fetchAndFormatDocument';
import { getFileNameAndExtension } from '@/utils/getFileNameAndExtension';
import { getFormattedFileName } from '@/utils/getFormattedFileName';
import { getHrefForImageViewer } from '@/utils/getHrefForImageViewer';
import { getUrlForViewer } from '@/utils/getUrlForViewer';
import { isImageForViewer } from '@/utils/isImage';
import { isViewable } from '@/utils/isViewable';
import {
  CommonErrorToast,
  DownloadToast,
  OpenNewTabToast,
} from '@/utils/toasts';

type Action = 'download' | 'open';
type LinkType = 'link' | 'originalDocument' | 'sentiment';

type ReturnType = {
  docDownloadHandler: () => void;
  getFileName: () => [string, string, string | undefined];
  getFolder: () => string | undefined;
  imageClickHandler: () => void;
  openLinkHandler: (linkType?: LinkType, newTab?: boolean) => void;
};

const showToast = (
  result: FeedItemType,
  action: Action,
  linkType?: LinkType
) => {
  const mime = result?.mime_type || result?.mime_types?.[0];
  if (!mime) return;

  if (action === 'download') {
    DownloadToast();
    return;
  }

  if (linkType && ['sentiment', 'originalDocument'].includes(linkType)) {
    OpenNewTabToast();
    return;
  }

  if (isViewable(mime)) {
    OpenNewTabToast();
  } else {
    DownloadToast();
  }
};

export const useBlobOperations = (
  result: FeedItemType | undefined
): ReturnType => {
  const [query] = useQueryParams();
  const popupBlockedMessaging = usePopupBlockedMessaging();
  const refetchReadDeeplinks = useRefetchReadDeeplinks();
  const { native } = useDevices();
  const searchTerm = query.q;

  const getFromQuery = React.useCallback(async () => {
    if (!result) {
      return null;
    }

    const { category, source, deeplink, access_key } = result;

    return fetchAndFormatDocument({ access_key, category, deeplink, source });
  }, [result]);

  const openInNewTab = React.useCallback(
    (href: string) => {
      if (native) {
        /**
         * In case of native, we need to open the signged s3 url in a new tab instead of opening it in the inapp PDF viewer
         *
         * This is because in a native app, we cannot open a new tab in the same window and we render the whole app in a webview
         */
        const url = new URL(href, window.location.origin);
        const params = new URLSearchParams(url.search);

        const link = JSON.parse(params.get('link') ?? '""');

        if (link) {
          window.open(link, '_blank', 'noreferrer noopener');
        }

        return;
      }

      const newWindown = window.open(href, '_blank', 'noreferrer noopener');

      //check if it is allowed to open a new tab
      if (
        !newWindown ||
        newWindown.closed ||
        typeof newWindown.closed == 'undefined'
      ) {
        popupBlockedMessaging();
      }
      refetchReadDeeplinks();
    },
    [native, popupBlockedMessaging, refetchReadDeeplinks]
  );

  const getFileName = React.useCallback((): [
    string,
    string,
    string | undefined
  ] => {
    if (!result) {
      return ['', '', ''];
    }

    if (result.original_filename && Array.isArray(result.original_filename)) {
      let formattedFilename = '';
      const originalFileName = result.original_filename[0];
      if (!originalFileName.includes('.')) {
        formattedFilename = getFormattedFileName(originalFileName);
        return [
          `${formattedFilename}.${result.mime_type}`,
          originalFileName,
          result.mime_type,
        ];
      }
      const [fileName, extension] = getFileNameAndExtension(
        originalFileName,
        result.mime_type
      );
      formattedFilename = getFormattedFileName(fileName);
      return [
        `${getFormattedFileName(fileName)}.${extension.toLowerCase()}`,
        fileName,
        extension,
      ];
    }
    if (result.company_name) return ['', result.company_name, result.mime_type];
    if (result.title) return ['', result.title, result.mime_type];
    return ['', result.doc_id, result.mime_type];
  }, [result]);

  const openLinkHandler = React.useCallback(
    async (linkType = 'link') => {
      if (!result) {
        return;
      }

      try {
        showToast(result, 'open', linkType);
        const fromQuery = await getFromQuery();

        if (fromQuery !== null) {
          const [, fileName, extension] = getFileName();

          let link = fromQuery.object_location || fromQuery.link_to_report;
          let href = link;
          let params = {
            ...fromQuery,
            searchTerm,
            category_tab: result.category_tab,
            fileType: linkType,
          };

          if (linkType === 'originalDocument') {
            link = fromQuery.original_document_link;
            const original_filename = fromQuery.original_document_name
              ? [fromQuery.original_document_name]
              : fromQuery.original_filename
              ? fromQuery.original_filename
              : [''];
            const mime = fromQuery.original_document_mime?.toLowerCase() ?? '';
            params = { ...params, original_filename, link };

            if (isImageForViewer(mime)) href = getUrlForViewer('image', params);
            else if (mime === 'pdf') href = getUrlForViewer('document', params);
            else href = fromQuery.object_location;
          } else {
            if (linkType === 'sentiment') link = fromQuery.sentiment_link;
            const mime = fromQuery.mime_type?.toLowerCase() ?? '';
            params = { ...params, link };

            if (mime === 'pdf' || link?.includes('pdf'))
              href = getUrlForViewer('document', params);
            else if (isImageForViewer(mime))
              href = getUrlForViewer('image', params);
            else {
              downloadUtil({
                url: link ?? '',
                fileName: `${fileName}.${extension}`,
              });
              return;
            }
          }
          if (href) {
            openInNewTab(href);
          }
        }
        refetchReadDeeplinks();
      } catch (e) {
        CommonErrorToast();
        trackEvent('error', { error: (e as Error).message });
      }
    },
    [
      result,
      getFromQuery,
      refetchReadDeeplinks,
      getFileName,
      searchTerm,
      openInNewTab,
    ]
  );

  const docDownloadHandler = React.useCallback(async () => {
    if (!result) {
      return;
    }

    const fromQuery = await getFromQuery();
    const [, fileName, extension] = getFileName();

    downloadDocument({
      result: fromQuery,
      fileName: `${fileName}.${extension}`,
      native,
    });
  }, [result, getFromQuery, getFileName, native]);

  const imageClickHandler = React.useCallback(() => {
    if (!result) {
      return;
    }

    const href = getHrefForImageViewer(result);
    if (href) {
      openInNewTab(href);
    } else {
      CommonErrorToast();
    }
  }, [openInNewTab, result]);

  const getFolder = React.useCallback(() => {
    if (!result) {
      return undefined;
    }
    if (!result.tags) {
      return undefined;
    }
    return result.tags.split(/ ?, ?/gi).slice(-1)[0];
  }, [result]);

  return {
    docDownloadHandler,
    getFileName,
    getFolder,
    imageClickHandler,
    openLinkHandler,
  };
};
